/**
 * Il dettaglio di una cassetta: la pagina che si apre inquadrando il QR.
 *
 * Non è una schermata del banco e non sta in `elenco.tsx`. Le schermate hanno
 * un menu, un ruolo e una sessione; questa si apre da un quadrato stampato su
 * un foglio, con un telefono, in magazzino o a fine linea, da chi la sessione
 * non ce l'ha. Per questo il guscio non la avvolge e la lettura passa un token
 * `null`: la rotta dall'altra parte è pubblica.
 *
 * L'avviso che conta è `superata`. Il foglio è carta e la carta resta attaccata:
 * una cassetta riscritta ha in giro un foglio che dichiara un contenuto che non
 * ha più, ed è quello che il prototipo chiama «l'errore peggiore». Il QR porta
 * l'identificativo della **scrittura**, quindi la pagina se ne accorge da sé.
 */

import { useQuery } from '@tanstack/react-query';
import { leggi } from '@errebi/sessione';
import { zDettaglioCassetta, type DettaglioCassetta } from '@errebi/shared';

import { Stato } from '../components/state';
import { GarmentPicture } from './receiving/booth';

/**
 * Due modi di chiedere lo stesso foglio, e la differenza è chi lo chiede.
 *
 * `id` è l'identificativo di **una scrittura**: ce l'ha chi inquadra il QR, e
 * porta con sé il diritto della pagina di dire «questo foglio è superato».
 * `cassetta` è il codice, e ce l'ha il banco, che apre la stessa pagina toccando
 * il nome di una tessera: lì non c'è nessun foglio di carta da smascherare, si
 * guarda la cassetta che sta sul bancale, quindi la risposta è l'ultimo foglio.
 */
export function DettaglioCassetta(p: { readonly id: string } | { readonly cassetta: string }) {
  const percorso = 'id' in p ? `/cassetta/${p.id}` : `/cassetta/di/${p.cassetta}`;
  const q = useQuery({
    queryKey: ['cassetta', percorso],
    // Token `null`: qui non c'è una sessione, e la rotta non ne chiede una.
    queryFn: () => leggi(percorso, null, zDettaglioCassetta),
    retry: false,
  });

  if (q.isPending) return <Stato quale="caricando" dice="Sto leggendo il foglio…" />;
  if (q.isError) {
    return (
      <Stato
        quale="sconosciuto"
        dice={
          "Questo foglio non risulta. Può essere di un'etichetta stampata prima di un riavvio, " +
          'oppure il codice è stato letto male: il contenuto è comunque scritto in chiaro sulla carta.'
        }
      />
    );
  }

  return <Foglio d={q.data} />;
}

function Foglio({ d }: { readonly d: DettaglioCassetta }) {
  return (
    <div className="cassetta-dettaglio">
      {d.superata && (
        <div className="cassetta-dettaglio__superata" role="alert">
          <strong>Questo foglio è superato.</strong> La cassetta {d.cassetta} è stata riscritta il{' '}
          {quando(d.superata.scrittaIl)} — è la riscrittura {d.superata.riscritture}. Quello che
          leggi qui sotto è il contenuto di quando è uscito questo foglio.{' '}
          <a href={d.superata.indirizzo}>Vedi il contenuto di adesso</a>
        </div>
      )}

      <h1 className="cassetta-dettaglio__cod">{d.cassetta}</h1>
      <p className="cassetta-dettaglio__chi">
        {d.cliente} · ordine {d.ordine}
      </p>
      <p className="cassetta-dettaglio__cosa">
        {d.articolo} · {d.colore}
        {d.neutra && ' · NEUTRO'}
      </p>
      <p className="cassetta-dettaglio__sku">{d.sku}</p>

      {/* Gli stessi tre ripieghi della cabina luce, e per la stessa domanda: chi
          è in magazzino col foglio in mano guarda l'immagine, non lo SKU. La
          foto nostra vince, poi la vista di catalogo, poi la sagoma — che qui
          conta più che al banco, perché il capo il telefono non ce l'ha in
          mano. */}
      <GarmentPicture
        foto={d.foto}
        anteprima={d.anteprima}
        colore={d.colore}
        genere={d.genere}
        alt={`${d.articolo} ${d.colore}`}
        className="cassetta-dettaglio__foto"
      />

      <h2 className="cassetta-dettaglio__titolo">{d.capi} capi</h2>
      <ul className="cassetta-dettaglio__taglie">
        {d.taglie.map((t) => (
          <li key={t.taglia}>
            <span className="cassetta-dettaglio__taglia">{t.taglia}</span>
            <span className="cassetta-dettaglio__quante">×{t.quante}</span>
          </li>
        ))}
      </ul>

      <h2 className="cassetta-dettaglio__titolo">
        {d.gruppi.length === 1 ? 'Una etichetta' : `${d.gruppi.length} etichette`}
      </h2>
      {/* Servono a controllare che dentro ci sia quello che le etichette dei
          capi dichiarano: il barcode di un gruppo è il gruppo, non l'articolo. */}
      <ul className="cassetta-dettaglio__gruppi">
        {d.gruppi.map((g) => (
          <li key={g.id}>
            <code>{g.id}</code> · {g.taglia} ×{g.quantita}
          </li>
        ))}
      </ul>

      <p className="cassetta-dettaglio__quando">
        Scritta il {quando(d.scrittaIl)} · riscrittura {d.riscritture}
      </p>
    </div>
  );
}

/** La data come si legge, non come viaggia. */
function quando(iso: string): string {
  const d = new Date(iso);
  const due = (n: number) => String(n).padStart(2, '0');
  return `${due(d.getDate())}/${due(d.getMonth() + 1)} alle ${due(d.getHours())}:${due(d.getMinutes())}`;
}
